/**
 * Wykrywanie nowej wersji aplikacji.
 *
 * Dwie niezależne drogi, bo jedna nie wystarcza:
 *
 * 1. **Service worker** — zgłasza gotową paczkę zdarzeniem `onNeedRefresh`.
 *    Działa wyłącznie w bezpiecznym kontekście, czyli po HTTPS albo na
 *    `localhost`.
 * 2. **Odpytywanie `version.json`** — porównuje wersję z serwera z tą, na
 *    której działa otwarta karta.
 *
 * Druga droga nie jest zapasem na wszelki wypadek. Aplikacja bywa wystawiona
 * po zwykłym HTTP pod adresem IP w sieci lokalnej, a wtedy przeglądarka nie
 * rejestruje service workera w ogóle — zmierzone: `isSecureContext=false`,
 * `'serviceWorker' in navigator === false`. Bez odpytywania mechanizm byłby
 * tam martwy.
 */

/** Plik z wersją, generowany przy budowaniu obok `index.html`. */
export const ADRES_WERSJI = '/version.json'

/**
 * Co ile sprawdzamy wersję.
 *
 * Pięć minut to kompromis: użytkownik dowie się o poprawce w rozsądnym czasie,
 * a zapytanie o kilkadziesiąt bajtów raz na pięć minut nie waży nic ani dla
 * przeglądarki, ani dla serwera.
 */
export const CZESTOTLIWOSC_MS = 5 * 60 * 1000

/** Wersja pracy lokalnej — przy niej nie ma czego porównywać. */
const WERSJA_DEWELOPERSKA = 'dev'

/**
 * Czy serwer ma inną wersję niż ta otwarta w karcie.
 *
 * Świadomie „inna”, a nie „nowsza”: wersja to skrót commita, więc nie da się
 * ich porównać na większe–mniejsze. Różnica oznacza, że karta trzyma kod,
 * którego już nie ma na serwerze — i tyle wystarczy.
 */
export function czyNowaWersja(biezaca: string, zdalna: string | null | undefined): boolean {
  if (!zdalna) return false
  if (biezaca === WERSJA_DEWELOPERSKA || zdalna === WERSJA_DEWELOPERSKA) return false
  return biezaca !== zdalna
}

/**
 * Pobiera wersję z serwera. Zwraca `null`, gdy się nie udało.
 *
 * Błąd sieci nie może niczego przerywać — brak odpowiedzi znaczy tyle, że tym
 * razem nie wiemy, a nie że coś jest nie tak z aplikacją.
 */
export async function pobierzWersje(signal?: AbortSignal): Promise<string | null> {
  try {
    // `no-store`, bo pytanie „czy jest nowa wersja” zadane pamięci podręcznej
    // zawsze zwróciłoby starą odpowiedź.
    const response = await fetch(ADRES_WERSJI, { cache: 'no-store', signal })
    if (!response.ok) return null
    const dane: unknown = await response.json()
    if (typeof dane === 'object' && dane !== null && 'version' in dane) {
      const wersja = (dane as { version: unknown }).version
      return typeof wersja === 'string' ? wersja : null
    }
    return null
  } catch {
    return null
  }
}

interface OpcjeSledzenia {
  biezaca: string
  onNowa: () => void
  interval?: number
}

/**
 * Sprawdza wersję na starcie i potem cyklicznie. Zwraca funkcję zatrzymującą.
 *
 * Sprawdzamy też przy powrocie do karty: najczęstszy scenariusz to laptop
 * zamknięty na noc i odblokowany rano, gdy w międzyczasie wyszła nowa wersja.
 */
export function sledzWersje({
  biezaca,
  onNowa,
  interval = CZESTOTLIWOSC_MS,
}: OpcjeSledzenia): () => void {
  const kontroler = new AbortController()
  let zatrzymane = false

  const sprawdz = async () => {
    if (zatrzymane || document.visibilityState === 'hidden') return
    const zdalna = await pobierzWersje(kontroler.signal)
    if (!zatrzymane && czyNowaWersja(biezaca, zdalna)) onNowa()
  }

  void sprawdz()
  const timer = window.setInterval(() => void sprawdz(), interval)
  const naPowrocie = () => void sprawdz()
  document.addEventListener('visibilitychange', naPowrocie)

  return () => {
    zatrzymane = true
    kontroler.abort()
    window.clearInterval(timer)
    document.removeEventListener('visibilitychange', naPowrocie)
  }
}

/**
 * Twarde odświeżenie: wyrejestrowanie service workera, czyszczenie pamięci
 * podręcznej i przeładowanie.
 *
 * Potrzebne, gdy o nowej wersji dowiedzieliśmy się z `version.json`, a service
 * worker jeszcze jej nie zauważył. Zwykłe `location.reload()` dostałoby wtedy
 * **starą** powłokę aplikacji prosto z jego pamięci podręcznej i okno
 * wracałoby w kółko. Zmierzone: bez tego kroku karta po odświeżeniu nadal
 * miała poprzednią wersję.
 *
 * W przeglądarce bez service workera obie operacje nic nie robią i zostaje
 * samo przeładowanie.
 */
export async function twardeOdswiezenie(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const rejestracje = await navigator.serviceWorker.getRegistrations()
      await Promise.all(rejestracje.map((rejestracja) => rejestracja.unregister()))
    }
    if ('caches' in window) {
      const nazwy = await caches.keys()
      await Promise.all(nazwy.map((nazwa) => caches.delete(nazwa)))
    }
  } catch {
    // Nieudane sprzątanie nie może zablokować przeładowania — bez niego
    // użytkownik zostaje z oknem, którego nie da się zamknąć na dobre.
  }
  window.location.reload()
}
